"use client"

import { useState, useRef, useEffect } from "react"
import {
  FileSpreadsheet, Camera, Package,
  Save, CheckCircle, AlertTriangle, MapPin, Layers, RefreshCw,
  Edit3, Tag, Building, XCircle, Image as ImageIcon,
  Plus, Info, PackageOpen, Trash2, Search,
  ArrowLeft, Globe, List, HelpCircle, Lock, CheckCircle2
} from 'lucide-react'

import BulkUploadModal from '../components/BulkUploadModal';
import ProductsList from './ProductsList';
import ProductosAB from './ProductosAB';
import GestionProveedores from './GestionProveedores';
import GestionFamilias from './GestionFamilias';
import GestionCategorias from './GestionCategorias';
import { authFetch } from "../utils/authFetch";

const RegisterProduct = ({ token }) => {
  const API_URL = process.env.REACT_APP_API_URL
    || (window.location.hostname === 'localhost'
      ? 'http://localhost:3001/api'
      : 'https://alvarezbohlback-production.up.railway.app/api');

  // Estados principales
  const [activeSection, setActiveSection] = useState("listado");
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  // Estados para formulario de registro
  const [formMode, setFormMode] = useState("nuevo");
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  // Estados para datos maestros
  const [proveedores, setProveedores] = useState([]);
  const [familias, setFamilias] = useState([]);
  const [subfamilias, setSubfamilias] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [subcategorias, setSubcategorias] = useState([]);

  // Estados para ubicaciones
  const [zonas, setZonas] = useState([]);
  const [lineas, setLineas] = useState([]);
  const [columnas, setColumnas] = useState([]);
  const [niveles, setNiveles] = useState([]);

  const [unidadesMedidaOptions] = useState([
    "Caja", "Display", "Unidad", "Tira", "Sachet", "Paquete", "Bulto", "BLS", "DPK", "SCH", 
    "Barra", "Bolsa", "Onza", "Mascarilla", "Pieza"
  ]);

  const [almacenesOptions] = useState([
    "01", "02", "ALM-BUENO", "ALM-MAL-ESTADO"
  ]);

  // Estados para tooltips
  const [activeTooltip, setActiveTooltip] = useState(null);

  // Estados para imagen del producto
  const [imageUploadMode, setImageUploadMode] = useState("file");
  const [imageUrl, setImageUrl] = useState("");
  const [imagePreview, setImagePreview] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imageFile, setImageFile] = useState(null);

  // Estados del formulario
  const [formData, setFormData] = useState({
    codigo_barras_mayor: "",
    codigo_barras_intermedia: "",
    codigo_barras_minima: "",
    cod_articulo: "",
    descripcion: "",
    proveedor_id: "",
    unidad_negocio_proveedor: "",
    codigo_articulo_proveedor: "",
    familia_id: "",
    subfamilia_id: "",
    categoria_id: "",
    subcategoria_id: "",
    unidad_mayor: "Caja",
    unidad_display: "Display",
    cantidad_display_por_unidad_mayor: "",
    unidad_base: "Unidad",
    cantidad_unidad_base_por_display: "",
    peso: "",
    volumen: "",
    costo_real: "",
    costo_promedio: "",
    price: "",
    cod_almacen: "",
    imagen_url: "",
    min_stock: "",
  });

  // Estados para lotes
  const [lotes, setLotes] = useState([
    {
      id: 1,
      lote: "",
      fecha_compra: "",
      fecha_vencimiento: "",
      unidad_max: "Caja",
      unidad_med: "Display",
      unidad_peq: "Unidad",
      cantidad_max: "",
      cantidad_med: "",
      cantidad_peq: "",
      stock_actual: 0,
      modo_ubicacion: "automatico",
      ubicacion_manual: {
        zona_id: "",
        linea_id: "",
        columna_id: "",
        nivel_id: ""
      }
    }
  ]);

  const [scanResult, setScanResult] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const fileInputRef = useRef(null);
  const productImageInputRef = useRef(null);

  useEffect(() => {
  if (token) {
    loadInitialData();
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [token]);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      await Promise.all([
        loadProveedores(),
        loadFamilias(),
        loadCategorias(),
        loadZonas(),
        loadProducts()
      ]);
      setLoading(false);
    } catch (err) {
      console.error('Error loading initial data:', err);
      setLoading(false);
    }
  };

  const loadProveedores = async () => {
    try {
      const response = await authFetch(`${API_URL}/proveedores`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response) return;
      const result = await response.json();
      if (result.success) {
        setProveedores(result.data);
      }
    } catch (err) {
      console.error('Error loading proveedores:', err);
    }
  };

  const loadFamilias = async () => {
    try {
      const response = await authFetch(`${API_URL}/familias`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response) return;
      const result = await response.json();
      if (result.success) {
        setFamilias(result.data);
      }
    } catch (err) {
      console.error('Error loading familias:', err);
    }
  };

  const loadCategorias = async () => {
    try {
      const response = await authFetch(`${API_URL}/categorias`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response) return;
      const result = await response.json();
      if (result.success) {
        setCategorias(result.data);
      }
    } catch (err) {
      console.error('Error loading categorias:', err);
    }
  };

  const loadZonas = async () => {
    try {
      const response = await authFetch(`${API_URL}/products/ubicaciones/zonas`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response) return;
      const result = await response.json();
      if (result.success) {
        setZonas(result.data);
      }
    } catch (err) {
      console.error('Error loading zonas:', err);
    }
  };

  const loadLineas = async (zonaId) => {
    try {
      const response = await authFetch(`${API_URL}/products/ubicaciones/lineas/${zonaId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response) return;
      const result = await response.json();
      if (result.success) {
        setLineas(result.data);
      }
    } catch (err) {
      console.error('Error loading lineas:', err);
    }
  };

  const loadColumnas = async (lineaId) => {
    try {
      const response = await authFetch(`${API_URL}/products/ubicaciones/columnas/${lineaId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response) return;
      const result = await response.json();
      if (result.success) {
        setColumnas(result.data);
      }
    } catch (err) {
      console.error('Error loading columnas:', err);
    }
  };

  const loadNiveles = async (columnaId) => {
    try {
      const response = await authFetch(`${API_URL}/products/ubicaciones/niveles/${columnaId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response) return;
      const result = await response.json();
      if (result.success) {
        setNiveles(result.data);
      }
    } catch (err) {
      console.error('Error loading niveles:', err);
    }
  };

  const loadProducts = async () => {
    try {
      const response = await authFetch(`${API_URL}/products`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response) return;
      const result = await response.json();
      if (result.success) {
        setProducts(result.data);
      }
    } catch (err) {
      console.error('Error loading products:', err);
    }
  };

  useEffect(() => {
    if (formData.familia_id) {
      loadSubfamilias(formData.familia_id);
    } else {
      setSubfamilias([]);
      setFormData(prev => ({ ...prev, subfamilia_id: "" }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.familia_id]);

  useEffect(() => {
    if (formData.categoria_id) {
      loadSubcategorias(formData.categoria_id);
    } else {
      setSubcategorias([]);
      setFormData(prev => ({ ...prev, subcategoria_id: "" }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.categoria_id]);

  const loadSubfamilias = async (familiaId) => {
    try {
      const response = await authFetch(`${API_URL}/subfamilias/familia/${familiaId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response) return;
      const result = await response.json();
      if (result.success) {
        setSubfamilias(result.data);
      }
    } catch (err) {
      console.error('Error loading subfamilias:', err);
    }
  };

  const loadSubcategorias = async (categoriaId) => {
    try {
      const response = await authFetch(`${API_URL}/subcategorias/categoria/${categoriaId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response) return;
      const result = await response.json();
      if (result.success) {
        setSubcategorias(result.data);
      }
    } catch (err) {
      console.error('Error loading subcategorias:', err);
    }
  };

  // Funciones de validación para activar secciones
  const isSection1Complete = () => {
    return formData.codigo_barras_minima.trim() !== "";
  };

  const isSection2Complete = () => {
    return isSection1Complete() && formData.proveedor_id !== "";
  };

  const isSection3Complete = () => {
    return isSection2Complete() && formData.categoria_id !== "";
  };

  const isSection4Complete = () => {
    return isSection3Complete() && scanResult !== null;
  };

  const isSection5Complete = () => {
    return isSection4Complete(); // Imagen no es obligatoria
  };

  // Función para obtener el nombre visual de la unidad
  const getUnidadDisplayName = (unidadValue, defaultName) => {
    if (!unidadValue || unidadValue === defaultName) {
      return defaultName;
    }
    return unidadValue;
  };

  const calcularStockLote = (lote) => {
    const cantidadMax = parseInt(lote.cantidad_max) || 0;
    const cantidadMed = parseInt(lote.cantidad_med) || 0;
    const cantidadPeq = parseInt(lote.cantidad_peq) || 0;
    
    const conversionMed = parseInt(formData.cantidad_display_por_unidad_mayor || selectedProduct?.cantidad_display_por_unidad_mayor) || 1;
    const conversionPeq = parseInt(formData.cantidad_unidad_base_por_display || selectedProduct?.cantidad_unidad_base_por_display) || 1;
    
    const stockDesdeMax = cantidadMax * conversionMed * conversionPeq;
    const stockDesdeMed = cantidadMed * conversionPeq;
    const stockDesdePeq = cantidadPeq;
    
    return stockDesdeMax + stockDesdeMed + stockDesdePeq;
  };

  const calcularStockTotal = () => {
    return lotes.reduce((total, lote) => {
      return total + calcularStockLote(lote);
    }, 0);
  };

  const agregarLote = () => {
    const newId = lotes.length > 0 ? Math.max(...lotes.map(l => l.id)) + 1 : 1;
    
    const unidadMayor = formMode === "nuevo" ? formData.unidad_mayor : selectedProduct?.unidad_mayor;
    const unidadDisplay = formMode === "nuevo" ? formData.unidad_display : selectedProduct?.unidad_display;
    const unidadBase = formMode === "nuevo" ? formData.unidad_base : selectedProduct?.unidad_base;

    setLotes([...lotes, {
      id: newId,
      lote: "",
      fecha_compra: "",
      fecha_vencimiento: "",
      unidad_max: unidadMayor || "Caja",
      unidad_med: unidadDisplay || "Display",
      unidad_peq: unidadBase || "Unidad",
      cantidad_max: "",
      cantidad_med: "",
      cantidad_peq: "",
      stock_actual: 0,
      modo_ubicacion: "automatico",
      ubicacion_manual: {
        zona_id: "",
        linea_id: "",
        columna_id: "",
        nivel_id: ""
      }
    }]);
  };

  const eliminarLote = (loteId) => {
    if (lotes.length <= 1) {
      alert('Debe haber al menos un lote');
      return;
    }
    setLotes(lotes.filter(l => l.id !== loteId));
  };

  const actualizarLote = (loteId, field, value) => {
    setLotes(lotes.map(l => {
      if (l.id === loteId) {
        if (field.startsWith('ubicacion_manual.')) {
          const subField = field.split('.')[1];
          const updatedLote = {
            ...l,
            ubicacion_manual: {
              ...l.ubicacion_manual,
              [subField]: value
            }
          };

          if (subField === 'zona_id' && value) {
            loadLineas(value);
            updatedLote.ubicacion_manual.linea_id = "";
            updatedLote.ubicacion_manual.columna_id = "";
            updatedLote.ubicacion_manual.nivel_id = "";
          } else if (subField === 'linea_id' && value) {
            loadColumnas(value);
            updatedLote.ubicacion_manual.columna_id = "";
            updatedLote.ubicacion_manual.nivel_id = "";
          } else if (subField === 'columna_id' && value) {
            loadNiveles(value);
            updatedLote.ubicacion_manual.nivel_id = "";
          }

          return updatedLote;
        } else {
          const updatedLote = { ...l, [field]: value };
          if (['cantidad_max', 'cantidad_med', 'cantidad_peq'].includes(field)) {
            updatedLote.stock_actual = calcularStockLote(updatedLote);
          }
          return updatedLote;
        }
      }
      return l;
    }));
  };

  useEffect(() => {
    if (formMode === "nuevo") {
      setLotes(prevLotes => prevLotes.map(lote => ({
        ...lote,
        unidad_max: formData.unidad_mayor || lote.unidad_max,
        unidad_med: formData.unidad_display || lote.unidad_med,
        unidad_peq: formData.unidad_base || lote.unidad_peq
      })));
    }
  }, [formData.unidad_mayor, formData.unidad_display, formData.unidad_base, formMode]);

  const analyzeImage = (file) => {
    setIsProcessing(true);
    setTimeout(() => {
      const fileName = file.name.toLowerCase();
      let result;
      if (fileName.includes("defect") || fileName.includes("damage") || fileName.includes("broken")) {
        result = {
          status: "defective",
          message: "Pallet dañado - Productos con defectos detectados",
          confidence: Math.floor(Math.random() * 15) + 85,
          issues: ["Embalaje dañado", "Etiquetas ilegibles"],
        };
      } else if (fileName.includes("ok") || fileName.includes("good") || fileName.includes("perfect")) {
        result = {
          status: "good",
          message: "Todo correcto - Productos en perfecto estado",
          confidence: Math.floor(Math.random() * 10) + 90,
          issues: [],
        };
      } else {
        const isGood = Math.random() > 0.3;
        result = {
          status: isGood ? "good" : "defective",
          message: isGood ? "Estado aceptable - Productos listos para almacenar" : "Problemas menores detectados",
          confidence: Math.floor(Math.random() * 20) + 75,
          issues: isGood ? [] : ["Ligero desplazamiento de carga"],
        };
      }
      setScanResult(result);
      setIsProcessing(false);
    }, 2000);
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setScanResult(null);
      analyzeImage(file);
    }
  };

  const handleProductImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        alert('La imagen no puede superar los 5MB');
        return;
      }

      const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        alert('Solo se permiten imágenes: JPEG, PNG, GIF, WEBP');
        return;
      }

      setImageFile(file);
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUploadImageFromFile = async () => {
    if (!imageFile) {
      alert('Por favor seleccione una imagen');
      return;
    }

    const barcode = formMode === "nuevo" ? formData.codigo_barras_minima : selectedProduct?.codigo_barras_minima;
    if (!barcode) {
      alert('Por favor ingrese el código de barras primero');
      return;
    }

    setUploadingImage(true);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('file', imageFile);
      formDataToSend.append('barcode', barcode);

      const response = await authFetch(`${API_URL}/products/upload-image`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formDataToSend
      });
      if (!response) return;

      const result = await response.json();

      if (result.success) {
        if (formMode === "nuevo") {
          setFormData(prev => ({ ...prev, imagen_url: result.data.imagen_url }));
        }
        alert('✅ Imagen subida correctamente');
      } else {
        alert('❌ Error: ' + result.error);
      }
    } catch (err) {
      console.error('Error uploading image:', err);
      alert('❌ Error al subir la imagen');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleUploadImageFromUrl = async () => {
    if (!imageUrl.trim()) {
      alert('Por favor ingrese una URL');
      return;
    }

    const barcode = formMode === "nuevo" ? formData.codigo_barras_minima : selectedProduct?.codigo_barras_minima;
    if (!barcode) {
      alert('Por favor ingrese el código de barras primero');
      return;
    }

    try {
      new URL(imageUrl);
    } catch {
      alert('URL inválida');
      return;
    }

    setUploadingImage(true);

    try {
      const response = await authFetch(`${API_URL}/products/image-from-url`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ image_url: imageUrl, barcode: barcode, use_direct_url: false })
      });
      if (!response) return;

      const result = await response.json();

      if (result.success) {
        if (formMode === "nuevo") {
          setFormData(prev => ({ ...prev, imagen_url: result.data.imagen_url }));
        }
        setImagePreview(result.data.imagen_url);
        alert('✅ Imagen cargada correctamente');
      } else {
        alert('❌ Error: ' + result.error);
      }
    } catch (err) {
      console.error('Error loading image from URL:', err);
      alert('❌ Error al cargar la imagen');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleRemoveImage = () => {
    setImageFile(null);
    setImagePreview(null);
    setImageUrl("");
    if (formMode === "nuevo") {
      setFormData(prev => ({ ...prev, imagen_url: "" }));
    }
    if (productImageInputRef.current) {
      productImageInputRef.current.value = "";
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectProduct = (product) => {
    setSelectedProduct(product);
    setImagePreview(product.imagen_url);
    setLotes([{
      id: 1,
      lote: "",
      fecha_compra: "",
      fecha_vencimiento: "",
      unidad_max: product.unidad_mayor || "Caja",
      unidad_med: product.unidad_display || "Display",
      unidad_peq: product.unidad_base || "Unidad",
      cantidad_max: "",
      cantidad_med: "",
      cantidad_peq: "",
      stock_actual: 0,
      modo_ubicacion: "automatico",
      ubicacion_manual: {
        zona_id: "",
        linea_id: "",
        columna_id: "",
        nivel_id: ""
      }
    }]);
  };

  const handleDeselectProduct = () => {
    setSelectedProduct(null);
    setImagePreview(null);
    setSearchTerm("");
    resetLotes();
  };

  const handleDeleteProductFromList = async (productId) => {
    const product = products.find(p => p.id === productId);
    
    if (!window.confirm(
      `¿Está seguro de eliminar el producto "${product.descripcion || product.codigo_barras_minima}"?\n\n` +
      `Esta acción eliminará:\n` +
      `• El producto\n` +
      `• Todos sus lotes (${product.stock_lotes?.lotes?.length || 0})\n` +
      `• Todas sus ubicaciones\n` +
      `• Stock total: ${product.stock_total || 0} unidades\n\n` +
      `Esta acción NO se puede deshacer.`
    )) {
      return;
    }

    try {
      const response = await authFetch(`${API_URL}/products/${productId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response) return;

      const result = await response.json();

      if (result.success) {
        alert('✅ Producto eliminado correctamente');
        await loadProducts();
      } else {
        alert('❌ Error: ' + result.error);
      }
    } catch (err) {
      console.error('Error deleting product:', err);
      alert('❌ Error al eliminar producto');
    }
  };

  const handleSave = async () => {
    if (formMode === "nuevo") {
      await handleSaveNewProduct();
    } else {
      await handleAddLotesToExisting();
    }
  };

  const handleSaveNewProduct = async () => {
    if (!formData.codigo_barras_minima || !formData.proveedor_id || !formData.categoria_id) {
      alert("Complete los campos requeridos del producto (código de barras mínima, proveedor, categoría)");
      return;
    }

    if (!formData.unidad_mayor) {
      alert("Debe especificar la unidad mayor");
      return;
    }

    if (!scanResult) {
      alert("Debe realizar el control de calidad (escanear imagen)");
      return;
    }

    for (const lote of lotes) {
      if (!lote.lote || !lote.fecha_vencimiento) {
        alert("Todos los lotes deben tener número de lote y fecha de vencimiento");
        return;
      }

      if (lote.modo_ubicacion === "manual") {
        if (!lote.ubicacion_manual.nivel_id) {
          alert(`El lote ${lote.lote} está en modo manual pero no tiene ubicación asignada`);
          return;
        }
      }
    }

    try {
      const ubicacionesManuales = {};
      lotes.forEach(lote => {
        if (lote.modo_ubicacion === "manual") {
          ubicacionesManuales[lote.lote] = lote.ubicacion_manual;
        }
      });

      const productData = {
        codigo_barras_mayor: formData.codigo_barras_mayor || null,
        codigo_barras_intermedia: formData.codigo_barras_intermedia || null,
        codigo_barras_minima: formData.codigo_barras_minima,
        cod_articulo: formData.cod_articulo || null,
        descripcion: formData.descripcion || null,
        proveedor_id: parseInt(formData.proveedor_id) || null,
        unidad_negocio_proveedor: formData.unidad_negocio_proveedor || null,
        codigo_articulo_proveedor: formData.codigo_articulo_proveedor || null,
        familia_id: parseInt(formData.familia_id) || null,
        subfamilia_id: parseInt(formData.subfamilia_id) || null,
        categoria_id: parseInt(formData.categoria_id) || null,
        subcategoria_id: parseInt(formData.subcategoria_id) || null,
        unidad_mayor: formData.unidad_mayor,
        unidad_display: formData.unidad_display || null,
        cantidad_display_por_unidad_mayor: parseInt(formData.cantidad_display_por_unidad_mayor) || 1,
        unidad_base: formData.unidad_base || null,
        cantidad_unidad_base_por_display: parseInt(formData.cantidad_unidad_base_por_display) || 1,
        peso: parseFloat(formData.peso) || null,
        volumen: parseFloat(formData.volumen) || null,
        costo_real: parseFloat(formData.costo_real) || null,
        costo_promedio: parseFloat(formData.costo_promedio) || null,
        price: parseFloat(formData.price) || null,
        cod_almacen: formData.cod_almacen || null,
        imagen_url: formData.imagen_url || null,
        min_stock: parseInt(formData.min_stock) || 0,
        scan_result: scanResult?.status || "good",
        issues: scanResult?.issues || [],
        confidence: scanResult?.confidence || 95,
        modo_ubicacion: lotes.some(l => l.modo_ubicacion === "manual") ? "manual" : "automatico",
        ubicacionesManuales,
        lotes: lotes.map(l => ({
          lote: l.lote,
          fecha_compra: l.fecha_compra || new Date().toISOString().split('T')[0],
          fecha_vencimiento: l.fecha_vencimiento,
          unidad_max: l.unidad_max || formData.unidad_mayor,
          unidad_med: l.unidad_med || formData.unidad_display,
          unidad_peq: l.unidad_peq || formData.unidad_base,
          cantidad_max: parseInt(l.cantidad_max) || 0,
          cantidad_med: parseInt(l.cantidad_med) || 0,
          cantidad_peq: parseInt(l.cantidad_peq) || 0
        }))
      };

      const response = await authFetch(`${API_URL}/products`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(productData)
      });
      if (!response) return;

      const result = await response.json();

      if (result.success) {
        let mensaje = `✅ Producto registrado correctamente

Stock Total: ${result.data.stock_total} unidades
Lotes: ${result.data.stock_lotes.lotes.length}
`;

        alert(mensaje);
        await loadProducts();
        resetForm();
      } else {
        alert('❌ Error: ' + result.error);
      }
    } catch (err) {
      console.error('Error saving product:', err);
      alert('❌ Error al registrar producto');
    }
  };

  const handleAddLotesToExisting = async () => {
    if (!selectedProduct) {
      alert('Debe seleccionar un producto');
      return;
    }

    for (const lote of lotes) {
      if (!lote.lote || !lote.fecha_vencimiento) {
        alert("Todos los lotes deben tener número de lote y fecha de vencimiento");
        return;
      }

      if (lote.modo_ubicacion === "manual") {
        if (!lote.ubicacion_manual.nivel_id) {
          alert(`El lote ${lote.lote} está en modo manual pero no tiene ubicación asignada`);
          return;
        }
      }
    }

    try {
      const ubicacionesManuales = {};
      lotes.forEach(lote => {
        if (lote.modo_ubicacion === "manual") {
          ubicacionesManuales[lote.lote] = lote.ubicacion_manual;
        }
      });

      const requestData = {
        modo_ubicacion: lotes.some(l => l.modo_ubicacion === "manual") ? "manual" : "automatico",
        ubicacionesManuales,
        lotes: lotes.map(l => ({
          lote: l.lote,
          fecha_compra: l.fecha_compra || new Date().toISOString().split('T')[0],
          fecha_vencimiento: l.fecha_vencimiento,
          unidad_max: l.unidad_max,
          unidad_med: l.unidad_med,
          unidad_peq: l.unidad_peq,
          cantidad_max: parseInt(l.cantidad_max) || 0,
          cantidad_med: parseInt(l.cantidad_med) || 0,
          cantidad_peq: parseInt(l.cantidad_peq) || 0
        }))
      };

      const response = await authFetch(`${API_URL}/products/add-lotes/${selectedProduct.codigo_barras_minima}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(requestData)
      });
      if (!response) return;

      const result = await response.json();

      if (result.success) {
        let mensaje = `✅ Lotes añadidos correctamente

Stock Total: ${result.data.stock_total} unidades
Total de Lotes: ${result.data.stock_lotes.lotes.length}
`;

        alert(mensaje);
        await loadProducts();
        handleDeselectProduct();
      } else {
        if (result.errores && result.errores.length > 0) {
          let errorMsg = '⚠️ Algunos lotes no se pudieron procesar:\n\n';
          result.errores.forEach(err => {
            errorMsg += `• ${err.lote}: ${err.error}\n`;
          });
          alert(errorMsg);
        } else {
          alert('❌ Error: ' + result.error);
        }
      }
    } catch (err) {
      console.error('Error adding lotes:', err);
      alert('❌ Error al añadir lotes');
    }
  };

  const resetForm = () => {
    setFormData({
      codigo_barras_mayor: "",
      codigo_barras_intermedia: "",
      codigo_barras_minima: "",
      cod_articulo: "",
      descripcion: "",
      proveedor_id: "",
      unidad_negocio_proveedor: "",
      codigo_articulo_proveedor: "",
      familia_id: "",
      subfamilia_id: "",
      categoria_id: "",
      subcategoria_id: "",
      unidad_mayor: "Caja",
      unidad_display: "Display",
      cantidad_display_por_unidad_mayor: "",
      unidad_base: "Unidad",
      cantidad_unidad_base_por_display: "",
      peso: "",
      volumen: "",
      costo_real: "",
      costo_promedio: "",
      price: "",
      cod_almacen: "",
      imagen_url: "",
      min_stock: "",
    });

    resetLotes();
    setScanResult(null);
    handleRemoveImage();
    
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const resetLotes = () => {
    setLotes([{
      id: 1,
      lote: "",
      fecha_compra: "",
      fecha_vencimiento: "",
      unidad_max: "Caja",
      unidad_med: "Display",
      unidad_peq: "Unidad",
      cantidad_max: "",
      cantidad_med: "",
      cantidad_peq: "",
      stock_actual: 0,
      modo_ubicacion: "automatico",
      ubicacion_manual: {
        zona_id: "",
        linea_id: "",
        columna_id: "",
        nivel_id: ""
      }
    }]);
  };

  const handleCloseGestion = async () => {
    await loadInitialData();
    setActiveSection("listado");
  };

  const filteredProducts = products.filter(p => {
    const search = searchTerm.toLowerCase();
    return (
      p.codigo_barras_minima?.toLowerCase().includes(search) ||
      p.codigo_barras_intermedia?.toLowerCase().includes(search) ||
      p.codigo_barras_mayor?.toLowerCase().includes(search) ||
      p.descripcion?.toLowerCase().includes(search) ||
      p.cod_articulo?.toLowerCase().includes(search)
    );
  });

  if (loading) {
    return (
      <div className="inventario-page">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Cargando datos...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="inventario-page">
      {/* Header */}
      <div className="page-header">
        <div className="header-content">
          <div className="title-section">
            <Package className="title-icon" />
            <h1>Gestión de Inventario</h1>
          </div>
        </div>

        {/* Navegación Principal */}
        <nav className="main-navigation">
          <button 
            className={`nav-item ${activeSection === 'listado' ? 'active' : ''}`}
            onClick={() => setActiveSection('listado')}
          >
            <List size={18} />
            <span>Listado de Productos</span>
          </button>
          <button 
            className={`nav-item ${activeSection === 'registro' ? 'active' : ''}`}
            onClick={() => setActiveSection('registro')}
          >
            <Package size={18} />
            <span>Registro Manual</span>
          </button>
          <button 
            className={`nav-item ${activeSection === 'carga-masiva' ? 'active' : ''}`}
            onClick={() => setActiveSection('carga-masiva')}
          >
            <FileSpreadsheet size={18} />
            <span>Carga Masiva</span>
          </button>
          <button 
            className={`nav-item ${activeSection === 'categorias' ? 'active' : ''}`}
            onClick={() => setActiveSection('categorias')}
          >
            <Layers size={18} />
            <span>Categorías</span>
          </button>
          <button 
            className={`nav-item ${activeSection === 'familias' ? 'active' : ''}`}
            onClick={() => setActiveSection('familias')}
          >
            <Tag size={18} />
            <span>Familias</span>
          </button>
          <button
            className={`nav-item ${activeSection === 'proveedores' ? 'active' : ''}`}
            onClick={() => setActiveSection('proveedores')}
          >
            <Building size={18} />
            <span>Proveedores</span>
          </button>
          <button
            className={`nav-item ${activeSection === 'pwms' ? 'active' : ''}`}
            onClick={() => setActiveSection('pwms')}
          >
            <Globe size={18} />
            <span>Sincronización</span>
          </button>
        </nav>
      </div>

      {/* Contenido */}
      <div className="page-content">
        {/* LISTADO */}
        {activeSection === 'listado' && (
          <div className="section-content">
            <ProductsList 
              onDeleteProduct={handleDeleteProductFromList}
              API_URL={API_URL}
              onProductsUpdate={loadProducts}
              token={token}
            />
          </div>
        )}

        {/* REGISTRO MANUAL */}
        {activeSection === 'registro' && (
          <div className="section-content">
            <div className="form-container">
              {/* PESTAÑAS COMO TABS */}
              <div className="form-tabs">
                <button
                  className={`form-tab ${formMode === 'nuevo' ? 'active' : ''}`}
                  onClick={() => {
                    setFormMode('nuevo');
                    handleDeselectProduct();
                  }}
                >
                  <Plus size={16} />
                  Crear Nuevo Producto
                </button>
                <button
                  className={`form-tab ${formMode === 'existente' ? 'active' : ''}`}
                  onClick={() => {
                    setFormMode('existente');
                    resetForm();
                  }}
                >
                  <PackageOpen size={16} />
                  Agregar Lotes a Existente
                </button>
              </div>

              {/* MODO: PRODUCTO EXISTENTE */}
              {formMode === 'existente' && (
                <div className="product-selection-section">
                  {!selectedProduct ? (
                    <div className="products-table-wrapper">
                      <div className="table-header">
                        <h3>Seleccionar Producto</h3>
                        <div className="search-box">
                          <Search size={16} />
                          <input
                            type="text"
                            placeholder="Buscar por código de barras, descripción..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                          />
                        </div>
                      </div>
                      
                      <div className="products-table-container">
                        <table className="products-table">
                          <thead>
                            <tr>
                              <th style={{ width: '60px' }}>Imagen</th>
                              <th>Cód. Barras (Mínima)</th>
                              <th>Descripción</th>
                              <th>Categoría</th>
                              <th>Stock</th>
                              <th>Lotes</th>
                              <th style={{ width: '60px' }}>Eliminar</th>
                            </tr>
                          </thead>
                          <tbody>
                            {filteredProducts.map((product) => (
                              <tr 
                                key={product.id}
                                className="selectable-row"
                              >
                                <td onClick={() => handleSelectProduct(product)}>
                                  <div className="product-image-cell">
                                    {product.imagen_url ? (
                                      <img src={product.imagen_url} alt={product.descripcion} />
                                    ) : (
                                      <div className="no-image">
                                        <ImageIcon size={20} />
                                      </div>
                                    )}
                                  </div>
                                </td>
                                <td className="barcode-cell" onClick={() => handleSelectProduct(product)}>{product.codigo_barras_minima}</td>
                                <td className="description-cell" onClick={() => handleSelectProduct(product)}>{product.descripcion || '-'}</td>
                                <td onClick={() => handleSelectProduct(product)}>{product.categorias?.nombre || '-'}</td>
                                <td className="stock-cell" onClick={() => handleSelectProduct(product)}>{product.stock_total || 0}</td>
                                <td className="lotes-cell" onClick={() => handleSelectProduct(product)}>{product.stock_lotes?.lotes?.length || 0}</td>
                                <td>
                                  <button
                                    className="btn-delete-inline"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDeleteProductFromList(product.id);
                                    }}
                                    title="Eliminar producto"
                                  >
                                    <Trash2 size={14} />
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>

                        {filteredProducts.length === 0 && (
                          <div className="no-results">
                            <Search size={32} />
                            <p>No se encontraron productos</p>
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="selected-product-view">
                      <div className="selected-product-header">
                        <button className="btn-back" onClick={handleDeselectProduct}>
                          <ArrowLeft size={16} />
                          Cambiar producto
                        </button>
                      </div>

                      <div className="selected-product-table">
                        <table className="compact-product-table">
                          <thead>
                            <tr>
                              <th style={{ width: '60px' }}>Imagen</th>
                              <th>Cód. Barras (Mínima)</th>
                              <th>Descripción</th>
                              <th>Categoría</th>
                              <th>Proveedor</th>
                              <th>Stock Total</th>
                              <th>Lotes</th>
                              <th>Unidad Mayor</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr className="selected-product-row">
                              <td>
                                <div className="product-image-cell-compact">
                                  {selectedProduct.imagen_url ? (
                                    <img src={selectedProduct.imagen_url} alt={selectedProduct.descripcion} />
                                  ) : (
                                    <div className="no-image-compact">
                                      <ImageIcon size={20} />
                                    </div>
                                  )}
                                </div>
                              </td>
                              <td className="barcode-compact">{selectedProduct.codigo_barras_minima}</td>
                              <td className="description-compact">{selectedProduct.descripcion || '-'}</td>
                              <td>{selectedProduct.categorias?.nombre || '-'}</td>
                              <td>{selectedProduct.proveedores?.nombre || '-'}</td>
                              <td className="stock-compact">{selectedProduct.stock_total || 0}</td>
                              <td className="lotes-compact">{selectedProduct.stock_lotes?.lotes?.length || 0}</td>
                              <td>{getUnidadDisplayName(selectedProduct.unidad_mayor, "Caja")}</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* CONTENIDO DEL FORMULARIO CON SECCIONES PROGRESIVAS */}
              <div className={`form-content ${formMode === 'existente' && !selectedProduct ? 'hidden' : ''}`}>
                <div className="form-layout-progressive">
                  {/* COLUMNA IZQUIERDA - Solo para NUEVO PRODUCTO */}
                  {formMode === 'nuevo' && (
                    <div className="form-column left-column">
                      {/* SECCIÓN 1: Información Básica - SIEMPRE ACTIVA */}
                      <section className={`form-section-progressive active`}>
                        <div className="section-header-progressive">
                          <div className="section-number active">1</div>
                          <div className="section-title-group">
                            <h2 className="section-title-progressive">Información Básica</h2>
                            <div className="section-status">
                              {isSection1Complete() ? (
                                <CheckCircle2 className="status-icon complete" size={16} />
                              ) : (
                                <span className="status-text">Campo obligatorio: Código de Barras - Unidad Mínima</span>
                              )}
                            </div>
                          </div>
                          <button
                            className="help-button"
                            onMouseEnter={() => setActiveTooltip('section1')}
                            onMouseLeave={() => setActiveTooltip(null)}
                          >
                            <HelpCircle size={18} />
                          </button>
                          {activeTooltip === 'section1' && (
                            <div className="tooltip-popup">
                              <p><strong>Información Básica del Producto</strong></p>
                              <p>Ingrese los datos identificadores principales:</p>
                              <ul>
                                <li><strong>Código de Barras - Unidad Mínima (*)</strong>: Identificador obligatorio del producto</li>
                                <li><strong>Código de Barras - Unidad Intermedia</strong>: Código de barras del display (opcional)</li>
                                <li><strong>Código de Barras - Unidad Mayor</strong>: Código de barras de la caja (opcional)</li>
                                <li><strong>Código de Artículo</strong>: Código interno opcional</li>
                                <li><strong>Descripción</strong>: Nombre descriptivo del producto</li>
                              </ul>
                            </div>
                          )}
                        </div>
                        <div className="barcodes-section">
                          <label className="info-label">
                            <Tag size={14} />
                            Códigos de Barras
                          </label>
                          <div className="form-grid">
                            <div className="form-group">
                              <label>Código de Barras - {formData.unidad_base || "Unidad"} <span className="required">*</span></label>
                              <input
                                type="text"
                                name="codigo_barras_minima"
                                placeholder="Ej: 8445291370630"
                                value={formData.codigo_barras_minima}
                                onChange={handleInputChange}
                                className="input"
                              />
                            </div>
                            <div className="form-group">
                              <label>Código de Barras - {formData.unidad_display || "Display"}</label>
                              <input
                                type="text"
                                name="codigo_barras_intermedia"
                                placeholder="Ej: 8445291371019"
                                value={formData.codigo_barras_intermedia}
                                onChange={handleInputChange}
                                className="input"
                              />
                            </div>
                            <div className="form-group">
                              <label>Código de Barras - {formData.unidad_mayor || "Caja"}</label>
                              <input
                                type="text"
                                name="codigo_barras_mayor"
                                placeholder="Ej: 8445291370647"
                                value={formData.codigo_barras_mayor}
                                onChange={handleInputChange}
                                className="input"
                              />
                            </div>
                          </div>
                        </div>
                        <div className="form-grid">
                          <div className="form-group">
                            <label>Código de Artículo</label>
                            <input
                              type="text"
                              name="cod_articulo"
                              placeholder="Código interno"
                              value={formData.cod_articulo}
                              onChange={handleInputChange}
                              className="input"
                            />
                          </div>
                          <div className="form-group full-width">
                            <label>Descripción</label>
                            <input
                              type="text"
                              name="descripcion"
                              placeholder="Descripción del producto"
                              value={formData.descripcion}
                              onChange={handleInputChange}
                              className="input"
                            />
                          </div>
                        </div>
                      </section>

                      {/* SECCIÓN 2: Proveedor */}
                      <section className={`form-section-progressive ${isSection1Complete() ? 'active' : 'locked'}`}>
                        <div className="section-header-progressive">
                          <div className={`section-number ${isSection1Complete() ? 'active' : ''}`}>2</div>
                          <div className="section-title-group">
                            <h2 className="section-title-progressive">Proveedor</h2>
                            {!isSection1Complete() && <Lock className="lock-icon" size={14} />}
                            {isSection1Complete() && (
                              <div className="section-status">
                                {isSection2Complete() ? (
                                  <CheckCircle2 className="status-icon complete" size={16} />
                                ) : (
                                  <span className="status-text">Campo obligatorio: Proveedor</span>
                                )}
                              </div>
                            )}
                          </div>
                          {isSection1Complete() && (
                            <button
                              className="help-button"
                              onMouseEnter={() => setActiveTooltip('section2')}
                              onMouseLeave={() => setActiveTooltip(null)}
                            >
                              <HelpCircle size={18} />
                            </button>
                          )}
                          {activeTooltip === 'section2' && (
                            <div className="tooltip-popup">
                              <p><strong>Información del Proveedor</strong></p>
                              <p>Asocie el producto con su proveedor:</p>
                              <ul>
                                <li><strong>Proveedor (*)</strong>: Empresa proveedora</li>
                                <li><strong>Unidad de Negocio</strong>: División del proveedor</li>
                                <li><strong>Código Artículo Proveedor</strong>: Referencia del proveedor</li>
                              </ul>
                            </div>
                          )}
                        </div>
                        <div className="form-grid">
                          <div className="form-group">
                            <label>Proveedor <span className="required">*</span></label>
                            <select
                              name="proveedor_id"
                              value={formData.proveedor_id}
                              onChange={handleInputChange}
                              className="input"
                              disabled={!isSection1Complete()}
                            >
                              <option value="">Seleccionar</option>
                              {proveedores.map((p) => (
                                <option key={p.id} value={p.id}>{p.nombre}</option>
                              ))}
                            </select>
                          </div>
                          <div className="form-group">
                            <label>Unidad de Negocio</label>
                            <input
                              type="text"
                              name="unidad_negocio_proveedor"
                              placeholder="Unidad de negocio"
                              value={formData.unidad_negocio_proveedor}
                              onChange={handleInputChange}
                              className="input"
                              disabled={!isSection1Complete()}
                            />
                          </div>
                          <div className="form-group full-width">
                            <label>Código Artículo Proveedor</label>
                            <input
                              type="text"
                              name="codigo_articulo_proveedor"
                              placeholder="Código del proveedor"
                              value={formData.codigo_articulo_proveedor}
                              onChange={handleInputChange}
                              className="input"
                              disabled={!isSection1Complete()}
                            />
                          </div>
                        </div>
                      </section>

                      {/* SECCIÓN 3: Clasificación */}
                      <section className={`form-section-progressive ${isSection2Complete() ? 'active' : 'locked'}`}>
                        <div className="section-header-progressive">
                          <div className={`section-number ${isSection2Complete() ? 'active' : ''}`}>3</div>
                          <div className="section-title-group">
                            <h2 className="section-title-progressive">Clasificación</h2>
                            {!isSection2Complete() && <Lock className="lock-icon" size={14} />}
                            {isSection2Complete() && (
                              <div className="section-status">
                                {isSection3Complete() ? (
                                  <CheckCircle2 className="status-icon complete" size={16} />
                                ) : (
                                  <span className="status-text">Campo obligatorio: Categoría</span>
                                )}
                              </div>
                            )}
                          </div>
                          {isSection2Complete() && (
                            <button
                              className="help-button"
                              onMouseEnter={() => setActiveTooltip('section3')}
                              onMouseLeave={() => setActiveTooltip(null)}
                            >
                              <HelpCircle size={18} />
                            </button>
                          )}
                          {activeTooltip === 'section3' && (
                            <div className="tooltip-popup">
                              <p><strong>Clasificación del Producto</strong></p>
                              <p>Organice el producto en categorías:</p>
                              <ul>
                                <li><strong>Familia</strong>: Agrupación principal</li>
                                <li><strong>SubFamilia</strong>: Subdivisión de familia</li>
                                <li><strong>Categoría (*)</strong>: Tipo de producto</li>
                                <li><strong>SubCategoría</strong>: Especificación de categoría</li>
                              </ul>
                            </div>
                          )}
                        </div>
                        <div className="form-grid">
                          <div className="form-group">
                            <label>Familia</label>
                            <select
                              name="familia_id"
                              value={formData.familia_id}
                              onChange={handleInputChange}
                              className="input"
                              disabled={!isSection2Complete()}
                            >
                              <option value="">Seleccionar</option>
                              {familias.map((f) => (
                                <option key={f.id} value={f.id}>{f.nombre}</option>
                              ))}
                            </select>
                          </div>
                          <div className="form-group">
                            <label>SubFamilia</label>
                            <select
                              name="subfamilia_id"
                              value={formData.subfamilia_id}
                              onChange={handleInputChange}
                              className="input"
                              disabled={!isSection2Complete() || !formData.familia_id}
                            >
                              <option value="">Seleccionar</option>
                              {subfamilias.map((s) => (
                                <option key={s.id} value={s.id}>{s.nombre}</option>
                              ))}
                            </select>
                          </div>
                          <div className="form-group">
                            <label>Categoría <span className="required">*</span></label>
                            <select
                              name="categoria_id"
                              value={formData.categoria_id}
                              onChange={handleInputChange}
                              className="input"
                              disabled={!isSection2Complete()}
                            >
                              <option value="">Seleccionar</option>
                              {categorias.map((c) => (
                                <option key={c.id} value={c.id}>{c.nombre}</option>
                              ))}
                            </select>
                          </div>
                          <div className="form-group">
                            <label>SubCategoría</label>
                            <select
                              name="subcategoria_id"
                              value={formData.subcategoria_id}
                              onChange={handleInputChange}
                              className="input"
                              disabled={!isSection2Complete() || !formData.categoria_id}
                            >
                              <option value="">Seleccionar</option>
                              {subcategorias.map((s) => (
                                <option key={s.id} value={s.id}>{s.nombre}</option>
                              ))}
                            </select>
                          </div>
                        </div>
                      </section>

                      {/* SECCIÓN 4: Control de Calidad */}
                      <section className={`form-section-progressive ${isSection3Complete() ? 'active' : 'locked'}`}>
                        <div className="section-header-progressive">
                          <div className={`section-number ${isSection3Complete() ? 'active' : ''}`}>4</div>
                          <div className="section-title-group">
                            <h2 className="section-title-progressive">Control de Calidad</h2>
                            {!isSection3Complete() && <Lock className="lock-icon" size={14} />}
                            {isSection3Complete() && (
                              <div className="section-status">
                                {isSection4Complete() ? (
                                  <CheckCircle2 className="status-icon complete" size={16} />
                                ) : (
                                  <span className="status-text">Requerido: Escaneo de calidad</span>
                                )}
                              </div>
                            )}
                          </div>
                          {isSection3Complete() && (
                            <button
                              className="help-button"
                              onMouseEnter={() => setActiveTooltip('section4')}
                              onMouseLeave={() => setActiveTooltip(null)}
                            >
                              <HelpCircle size={18} />
                            </button>
                          )}
                          {activeTooltip === 'section4' && (
                            <div className="tooltip-popup">
                              <p><strong>Control de Calidad (*)</strong></p>
                              <p>Verifica el estado del producto mediante análisis de imagen:</p>
                              <ul>
                                <li>Capture una foto del pallet o producto</li>
                                <li>El sistema analizará automáticamente</li>
                                <li>Se detectarán defectos o anomalías</li>
                                <li>Resultado: Óptimo o Defectuoso</li>
                              </ul>
                            </div>
                          )}
                        </div>
                        
                        <div className="scan-box">
                          <input
                            type="file"
                            ref={fileInputRef}
                            onChange={handleImageUpload}
                            accept="image/*"
                            className="file-hidden"
                            disabled={!isSection3Complete()}
                          />
                          
                          {!scanResult && !isProcessing && (
                            <div 
                              className={`scan-dropzone ${!isSection3Complete() ? 'disabled' : ''}`}
                              onClick={() => isSection3Complete() && fileInputRef.current?.click()}
                            >
                              <Camera size={32} />
                              <p>Escanear Producto</p>
                              <span>Captura una imagen para análisis</span>
                            </div>
                          )}

                          {isProcessing && (
                            <div className="scan-processing">
                              <div className="spinner"></div>
                              <span>Analizando imagen...</span>
                            </div>
                          )}

                          {scanResult && !isProcessing && (
                            <div className={`scan-result ${scanResult.status}`}>
                              <div className="result-icon">
                                {scanResult.status === "good" ? (
                                  <CheckCircle size={24} />
                                ) : (
                                  <AlertTriangle size={24} />
                                )}
                              </div>
                              <div className="result-text">
                                <strong>{scanResult.message}</strong>
                                <span className="confidence">{scanResult.confidence}% confianza</span>
                              </div>
                              <button 
                                className="rescan-btn"
                                onClick={() => fileInputRef.current?.click()}
                                type="button"
                              >
                                <RefreshCw size={14} />
                              </button>
                            </div>
                          )}
                        </div>
                      </section>

                      {/* SECCIÓN 5: Imagen del Producto */}
                      <section className={`form-section-progressive ${isSection4Complete() ? 'active' : 'locked'}`}>
                        <div className="section-header-progressive">
                          <div className={`section-number ${isSection4Complete() ? 'active' : ''}`}>5</div>
                          <div className="section-title-group">
                            <h2 className="section-title-progressive">Imagen del Producto</h2>
                            {!isSection4Complete() && <Lock className="lock-icon" size={14} />}
                            {isSection4Complete() && (
                              <div className="section-status">
                                <span className="status-text optional">Opcional</span>
                              </div>
                            )}
                          </div>
                          {isSection4Complete() && (
                            <button
                              className="help-button"
                              onMouseEnter={() => setActiveTooltip('section5')}
                              onMouseLeave={() => setActiveTooltip(null)}
                            >
                              <HelpCircle size={18} />
                            </button>
                          )}
                          {activeTooltip === 'section5' && (
                            <div className="tooltip-popup">
                              <p><strong>Imagen del Producto</strong></p>
                              <p>Agregue una imagen representativa del producto:</p>
                              <ul>
                                <li><strong>Archivo</strong>: Suba desde su dispositivo (JPG, PNG, GIF, WEBP)</li>
                                <li><strong>URL</strong>: Proporcione un enlace directo</li>
                                <li>Tamaño máximo: 5MB</li>
                              </ul>
                            </div>
                          )}
                        </div>
                        
                        <div className="image-upload-box">
                          <div className="upload-mode-switch">
                            <button
                              className={`mode-option ${imageUploadMode === "file" ? "active" : ""}`}
                              onClick={() => setImageUploadMode("file")}
                              type="button"
                              disabled={!isSection4Complete()}
                            >
                              Archivo
                            </button>
                            <button
                              className={`mode-option ${imageUploadMode === "url" ? "active" : ""}`}
                              onClick={() => setImageUploadMode("url")}
                              type="button"
                              disabled={!isSection4Complete()}
                            >
                              URL
                            </button>
                          </div>

                          {(imagePreview || formData.imagen_url) ? (
                            <div className="image-preview-wrapper">
                              <img src={imagePreview || formData.imagen_url} alt="Preview" />
                              <button className="remove-image" onClick={handleRemoveImage} type="button">
                                <XCircle size={18} />
                              </button>
                            </div>
                          ) : (
                            <>
                              {imageUploadMode === "file" && (
                                <div className="upload-dropzone">
                                  <input
                                    type="file"
                                    ref={productImageInputRef}
                                    onChange={handleProductImageSelect}
                                    accept="image/*"
                                    className="file-hidden"
                                    disabled={!isSection4Complete()}
                                  />
                                  <div 
                                    className={`dropzone-content ${!isSection4Complete() ? 'disabled' : ''}`}
                                    onClick={() => isSection4Complete() && productImageInputRef.current?.click()}
                                  >
                                    <ImageIcon size={32} />
                                    <p>Seleccionar imagen</p>
                                    <span>JPG, PNG, GIF, WEBP (máx. 5MB)</span>
                                  </div>
                                  {imageFile && !formData.imagen_url && (
                                    <button
                                      className="upload-confirm"
                                      onClick={handleUploadImageFromFile}
                                      disabled={uploadingImage || !isSection4Complete()}
                                      type="button"
                                    >
                                      {uploadingImage ? 'Subiendo...' : 'Subir Imagen'}
                                    </button>
                                  )}
                                </div>
                              )}

                              {imageUploadMode === "url" && (
                                <div className="url-upload">
                                  <input
                                    type="url"
                                    placeholder="https://ejemplo.com/imagen.jpg"
                                    value={imageUrl}
                                    onChange={(e) => setImageUrl(e.target.value)}
                                    className="input"
                                    disabled={!isSection4Complete()}
                                  />
                                  <button
                                    className="upload-confirm"
                                    onClick={handleUploadImageFromUrl}
                                    disabled={uploadingImage || !imageUrl.trim() || !isSection4Complete()}
                                    type="button"
                                  >
                                    {uploadingImage ? 'Cargando...' : 'Cargar'}
                                  </button>
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      </section>
                    </div>
                  )}

                  {/* COLUMNA DERECHA - SIEMPRE VISIBLE */}
                  <div className={`form-column right-column ${formMode === 'existente' ? 'full-width' : ''}`}>
                    {/* SECCIÓN: Unidades de Medida (solo nuevo producto) */}
                    {formMode === 'nuevo' && (
                      <section className={`form-section-progressive ${isSection3Complete() ? 'active' : 'locked'}`}>
                        <div className="section-header-progressive">
                          <div className={`section-number ${isSection3Complete() ? 'active' : ''}`}>6</div>
                          <div className="section-title-group">
                            <h2 className="section-title-progressive">Unidades de Medida</h2>
                            {!isSection3Complete() && <Lock className="lock-icon" size={14} />}
                            {isSection3Complete() && (
                              <div className="section-status">
                                {formData.unidad_mayor ? (
                                  <CheckCircle2 className="status-icon complete" size={16} />
                                ) : (
                                  <span className="status-text">Configure las unidades</span>
                                )}
                              </div>
                            )}
                          </div>
                          {isSection3Complete() && (
                            <button
                              className="help-button"
                              onMouseEnter={() => setActiveTooltip('section6')}
                              onMouseLeave={() => setActiveTooltip(null)}
                            >
                              <HelpCircle size={18} />
                            </button>
                          )}
                          {activeTooltip === 'section6' && (
                            <div className="tooltip-popup">
                              <p><strong>Jerarquía de Unidades</strong></p>
                              <p>Configure las unidades de medida jerárquicas:</p>
                              <ul>
                                <li><strong>Caja</strong>: Unidad mayor (ej: caja)</li>
                                <li><strong>Display</strong>: Unidad media (ej: paquete)</li>
                                <li><strong>Unidad</strong>: Unidad pequeña individual</li>
                              </ul>
                              <p className="help-note">Ejemplo: 1 Caja = 10 Displays = 240 Unidades</p>
                            </div>
                          )}
                        </div>
                        <div className="form-grid-3">
                          <div className="form-group">
                            <label>{getUnidadDisplayName(formData.unidad_mayor, "Caja")}</label>
                            <select
                              name="unidad_mayor"
                              value={formData.unidad_mayor}
                              onChange={handleInputChange}
                              className="input"
                              disabled={!isSection3Complete()}
                            >
                              {unidadesMedidaOptions.map((u) => (
                                <option key={u} value={u}>{u}</option>
                              ))}
                            </select>
                          </div>
                          
                          <div className="form-group">
                            <label>{getUnidadDisplayName(formData.unidad_display, "Display")}</label>
                            <select
                              name="unidad_display"
                              value={formData.unidad_display}
                              onChange={handleInputChange}
                              className="input"
                              disabled={!isSection3Complete()}
                            >
                              {unidadesMedidaOptions.map((u) => (
                                <option key={u} value={u}>{u}</option>
                              ))}
                            </select>
                          </div>

                          <div className="form-group">
                            <label>{getUnidadDisplayName(formData.unidad_base, "Unidad")}</label>
                            <select
                              name="unidad_base"
                              value={formData.unidad_base}
                              onChange={handleInputChange}
                              className="input"
                              disabled={!isSection3Complete()}
                            >
                              {unidadesMedidaOptions.map((u) => (
                                <option key={u} value={u}>{u}</option>
                              ))}
                            </select>
                          </div>
                        </div>

                        <div className="conversion-section">
                          <label className="info-label">
                            <Info size={14} />
                            Conversiones (para cálculo de stock)
                          </label>
                          <div className="form-grid-2">
                            <div className="form-group">
                              <label>Cantidad de {getUnidadDisplayName(formData.unidad_display, "displays")} por {getUnidadDisplayName(formData.unidad_mayor, "caja")}</label>
                              <input
                                type="number"
                                name="cantidad_display_por_unidad_mayor"
                                placeholder="1"
                                value={formData.cantidad_display_por_unidad_mayor}
                                onChange={handleInputChange}
                                className="input"
                                min="1"
                                disabled={!isSection3Complete()}
                              />
                            </div>
                            <div className="form-group">
                              <label>Cantidad de {getUnidadDisplayName(formData.unidad_base, "unidades")} por {getUnidadDisplayName(formData.unidad_display, "display")}</label>
                              <input
                                type="number"
                                name="cantidad_unidad_base_por_display"
                                placeholder="1"
                                value={formData.cantidad_unidad_base_por_display}
                                onChange={handleInputChange}
                                className="input"
                                min="1"
                                disabled={!isSection3Complete()}
                              />
                            </div>
                          </div>
                        </div>

                        <div className="form-group">
                          <label>Stock Mínimo ({getUnidadDisplayName(formData.unidad_base, "unidades")})</label>
                          <input
                            type="number"
                            name="min_stock"
                            placeholder="0"
                            value={formData.min_stock}
                            onChange={handleInputChange}
                            className="input"
                            min="0"
                            disabled={!isSection3Complete()}
                          />
                        </div>
                      </section>
                    )}

                    {/* SECCIÓN: Costos y Medidas (solo nuevo producto) */}
                    {formMode === 'nuevo' && (
                      <section className={`form-section-progressive ${isSection5Complete() ? 'active' : 'locked'}`}>
                        <div className="section-header-progressive">
                          <div className={`section-number ${isSection5Complete() ? 'active' : ''}`}>7</div>
                          <div className="section-title-group">
                            <h2 className="section-title-progressive">Costos y Medidas</h2>
                            {!isSection5Complete() && <Lock className="lock-icon" size={14} />}
                            {isSection5Complete() && (
                              <div className="section-status">
                                <span className="status-text optional">Opcional</span>
                              </div>
                            )}
                          </div>
                          {isSection5Complete() && (
                            <button
                              className="help-button"
                              onMouseEnter={() => setActiveTooltip('section7')}
                              onMouseLeave={() => setActiveTooltip(null)}
                            >
                              <HelpCircle size={18} />
                            </button>
                          )}
                          {activeTooltip === 'section7' && (
                            <div className="tooltip-popup">
                              <p><strong>Información Económica y Física</strong></p>
                              <p>Datos adicionales del producto:</p>
                              <ul>
                                <li><strong>Costo Real</strong>: Precio de compra actual</li>
                                <li><strong>Costo Promedio</strong>: Costo promedio histórico</li>
                                <li><strong>Precio de Venta</strong>: Precio al cliente</li>
                                <li><strong>Peso/Volumen</strong>: Dimensiones físicas</li>
                                <li><strong>Almacén</strong>: Ubicación de almacenamiento</li>
                              </ul>
                            </div>
                          )}
                        </div>
                        <div className="form-grid">
                          <div className="form-group">
                            <label>Costo Real</label>
                            <input
                              type="number"
                              name="costo_real"
                              placeholder="0.00"
                              value={formData.costo_real}
                              onChange={handleInputChange}
                              className="input"
                              min="0"
                              step="0.01"
                              disabled={!isSection5Complete()}
                            />
                          </div>
                          <div className="form-group">
                            <label>Costo Promedio</label>
                            <input
                              type="number"
                              name="costo_promedio"
                              placeholder="0.00"
                              value={formData.costo_promedio}
                              onChange={handleInputChange}
                              className="input"
                              min="0"
                              step="0.01"
                              disabled={!isSection5Complete()}
                            />
                          </div>
                          <div className="form-group">
                            <label>Precio de Venta</label>
                            <input
                              type="number"
                              name="price"
                              placeholder="0.00"
                              value={formData.price}
                              onChange={handleInputChange}
                              className="input"
                              min="0"
                              step="0.01"
                              disabled={!isSection5Complete()}
                            />
                          </div>
                          <div className="form-group">
                            <label>Peso (kg)</label>
                            <input
                              type="number"
                              name="peso"
                              placeholder="0.00"
                              value={formData.peso}
                              onChange={handleInputChange}
                              className="input"
                              min="0"
                              step="0.01"
                              disabled={!isSection5Complete()}
                            />
                          </div>
                          <div className="form-group">
                            <label>Volumen (cm³)</label>
                            <input
                              type="number"
                              name="volumen"
                              placeholder="0.00"
                              value={formData.volumen}
                              onChange={handleInputChange}
                              className="input"
                              min="0"
                              step="0.01"
                              disabled={!isSection5Complete()}
                            />
                          </div>
                          <div className="form-group">
                            <label>Código de Almacén</label>
                            <select
                              name="cod_almacen"
                              value={formData.cod_almacen}
                              onChange={handleInputChange}
                              className="input"
                              disabled={!isSection5Complete()}
                            >
                              <option value="">Seleccionar</option>
                              {almacenesOptions.map((a) => (
                                <option key={a} value={a}>{a}</option>
                              ))}
                            </select>
                          </div>
                        </div>
                      </section>
                    )}

                    {/* SECCIÓN: Gestión de Lotes */}
                    <section className={`form-section-progressive lotes-section ${(formMode === 'nuevo' ? isSection5Complete() : true) ? 'active' : 'locked'}`}>
                      <div className="section-header-progressive">
                        <div className={`section-number ${(formMode === 'nuevo' ? isSection5Complete() : true) ? 'active' : ''}`}>
                          {formMode === 'nuevo' ? '8' : '1'}
                        </div>
                        <div className="section-title-group">
                          <h2 className="section-title-progressive">Gestión de Lotes</h2>
                          {formMode === 'nuevo' && !isSection5Complete() && <Lock className="lock-icon" size={14} />}
                          {(formMode === 'nuevo' ? isSection5Complete() : true) && (
                            <div className="section-status">
                              <span className="status-text">Mínimo 1 lote requerido</span>
                            </div>
                          )}
                        </div>
                        {(formMode === 'nuevo' ? isSection5Complete() : true) && (
                          <>
                            <button
                              className="help-button"
                              onMouseEnter={() => setActiveTooltip('section8')}
                              onMouseLeave={() => setActiveTooltip(null)}
                            >
                              <HelpCircle size={18} />
                            </button>
                            <button
                              type="button"
                              className="btn-add-lote-header"
                              onClick={agregarLote}
                            >
                              <Plus size={16} />
                              Agregar Lote
                            </button>
                          </>
                        )}
                        {activeTooltip === 'section8' && (
                          <div className="tooltip-popup">
                            <p><strong>Gestión de Lotes</strong></p>
                            <p>Configure los lotes de ingreso del producto:</p>
                            <ul>
                              <li><strong>Número de Lote (*)</strong>: Identificador del lote</li>
                              <li><strong>Fechas</strong>: Compra y vencimiento</li>
                              <li><strong>Cantidades</strong>: Por cada nivel de unidad</li>
                              <li><strong>Ubicación</strong>: Automática o manual</li>
                            </ul>
                            <p className="help-note">El sistema calculará automáticamente el stock en unidades pequeñas</p>
                          </div>
                        )}
                      </div>

                      <div className="lotes-container">
                        {lotes.map((lote, index) => (
                          <div key={lote.id} className="lote-card">
                            <div className="lote-header">
                              <div className="lote-title">
                                <Package size={16} />
                                <span>Lote #{index + 1}</span>
                                <span className="stock-badge">{calcularStockLote(lote)} uds</span>
                              </div>
                              {lotes.length > 1 && (
                                <button
                                  type="button"
                                  className="btn-delete-lote"
                                  onClick={() => eliminarLote(lote.id)}
                                >
                                  <Trash2 size={14} />
                                </button>
                              )}
                            </div>

                            <div className="lote-body">
                              <div className="form-grid-2">
                                <div className="form-group">
                                  <label>Número de Lote <span className="required">*</span></label>
                                  <input
                                    type="text"
                                    placeholder="LOTE001"
                                    value={lote.lote}
                                    onChange={(e) => actualizarLote(lote.id, 'lote', e.target.value)}
                                    className="input"
                                    disabled={formMode === 'nuevo' && !isSection5Complete()}
                                  />
                                </div>

                                <div className="form-group">
                                  <label>Fecha de Compra</label>
                                  <input
                                    type="date"
                                    value={lote.fecha_compra}
                                    onChange={(e) => actualizarLote(lote.id, 'fecha_compra', e.target.value)}
                                    className="input"
                                    disabled={formMode === 'nuevo' && !isSection5Complete()}
                                  />
                                </div>

                                <div className="form-group full-width">
                                  <label>Fecha de Vencimiento <span className="required">*</span></label>
                                  <input
                                    type="date"
                                    value={lote.fecha_vencimiento}
                                    onChange={(e) => actualizarLote(lote.id, 'fecha_vencimiento', e.target.value)}
                                    className="input"
                                    disabled={formMode === 'nuevo' && !isSection5Complete()}
                                  />
                                </div>
                              </div>

                              <div className="cantidades-grid">
                                <div className="cantidad-item">
                                  <label>
                                    {getUnidadDisplayName(lote.unidad_max, "Caja")}
                                  </label>
                                  <input
                                    type="number"
                                    placeholder="0"
                                    value={lote.cantidad_max}
                                    onChange={(e) => actualizarLote(lote.id, 'cantidad_max', e.target.value)}
                                    className="input"
                                    min="0"
                                    disabled={formMode === 'nuevo' && !isSection5Complete()}
                                  />
                                </div>

                                <div className="cantidad-item">
                                  <label>
                                    {getUnidadDisplayName(lote.unidad_med, "Display")}
                                  </label>
                                  <input
                                    type="number"
                                    placeholder="0"
                                    value={lote.cantidad_med}
                                    onChange={(e) => actualizarLote(lote.id, 'cantidad_med', e.target.value)}
                                    className="input"
                                    min="0"
                                    disabled={formMode === 'nuevo' && !isSection5Complete()}
                                  />
                                </div>

                                <div className="cantidad-item">
                                  <label>
                                    {getUnidadDisplayName(lote.unidad_peq, "Unidad")}
                                  </label>
                                  <input
                                    type="number"
                                    placeholder="0"
                                    value={lote.cantidad_peq}
                                    onChange={(e) => actualizarLote(lote.id, 'cantidad_peq', e.target.value)}
                                    className="input"
                                    min="0"
                                    disabled={formMode === 'nuevo' && !isSection5Complete()}
                                  />
                                </div>
                              </div>

                              <div className="ubicacion-mode-section">
                                <label className="info-label">
                                  <MapPin size={14} />
                                  Asignación de Ubicación
                                </label>
                                
                                <div className="ubicacion-mode-switch">
                                  <button
                                    type="button"
                                    className={`mode-btn ${lote.modo_ubicacion === 'automatico' ? 'active' : ''}`}
                                    onClick={() => actualizarLote(lote.id, 'modo_ubicacion', 'automatico')}
                                    disabled={formMode === 'nuevo' && !isSection5Complete()}
                                  >
                                    <Globe size={14} />
                                    Automática
                                  </button>
                                  <button
                                    type="button"
                                    className={`mode-btn ${lote.modo_ubicacion === 'manual' ? 'active' : ''}`}
                                    onClick={() => actualizarLote(lote.id, 'modo_ubicacion', 'manual')}
                                    disabled={formMode === 'nuevo' && !isSection5Complete()}
                                  >
                                    <Edit3 size={14} />
                                    Manual
                                  </button>
                                </div>

                                {lote.modo_ubicacion === 'manual' && (
                                  <div className="manual-ubicacion-form">
                                    <div className="form-grid-2">
                                      <div className="form-group">
                                        <label>Zona <span className="required">*</span></label>
                                        <select
                                          value={lote.ubicacion_manual.zona_id}
                                          onChange={(e) => actualizarLote(lote.id, 'ubicacion_manual.zona_id', e.target.value)}
                                          className="input small"
                                          disabled={formMode === 'nuevo' && !isSection5Complete()}
                                        >
                                          <option value="">Seleccionar</option>
                                          {zonas.map((z) => (
                                            <option key={z.id} value={z.id}>{z.codigo}</option>
                                          ))}
                                        </select>
                                      </div>

                                      <div className="form-group">
                                        <label>Línea <span className="required">*</span></label>
                                        <select
                                          value={lote.ubicacion_manual.linea_id}
                                          onChange={(e) => actualizarLote(lote.id, 'ubicacion_manual.linea_id', e.target.value)}
                                          className="input small"
                                          disabled={!lote.ubicacion_manual.zona_id || (formMode === 'nuevo' && !isSection5Complete())}
                                        >
                                          <option value="">Seleccionar</option>
                                          {lineas.map((c) => (
                                            <option key={c.id} value={c.id}>Línea {c.numero}</option>
                                          ))}
                                        </select>
                                      </div>

                                      <div className="form-group">
                                        <label>Columna <span className="required">*</span></label>
                                        <select
                                          value={lote.ubicacion_manual.columna_id}
                                          onChange={(e) => actualizarLote(lote.id, 'ubicacion_manual.columna_id', e.target.value)}
                                          className="input small"
                                          disabled={!lote.ubicacion_manual.linea_id || (formMode === 'nuevo' && !isSection5Complete())}
                                        >
                                          <option value="">Seleccionar</option>
                                          {columnas.map((r) => (
                                            <option key={r.id} value={r.id}>Columna {r.numero}</option>
                                          ))}
                                        </select>
                                      </div>

                                      <div className="form-group">
                                        <label>Nivel <span className="required">*</span></label>
                                        <select
                                          value={lote.ubicacion_manual.nivel_id}
                                          onChange={(e) => actualizarLote(lote.id, 'ubicacion_manual.nivel_id', e.target.value)}
                                          className="input small"
                                          disabled={!lote.ubicacion_manual.columna_id || (formMode === 'nuevo' && !isSection5Complete())}
                                        >
                                          <option value="">Seleccionar</option>
                                          {niveles.map((n) => (
                                            <option key={n.id} value={n.id}>Nivel {n.numero}</option>
                                          ))}
                                        </select>
                                      </div>
                                    </div>
                                  </div>
                                )}

                                {lote.modo_ubicacion === 'automatico' && (
                                  <div className="ubicacion-auto-info">
                                    <Info size={12} />
                                    <span>La ubicación se asignará automáticamente según disponibilidad</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>

                      <div className="lotes-resumen">
                        <div className="resumen-row">
                          <span className="resumen-label">Total de Lotes:</span>
                          <span className="resumen-value">{lotes.length}</span>
                        </div>
                        <div className="resumen-row highlighted">
                          <span className="resumen-label">Stock a Añadir:</span>
                          <span className="resumen-value">{calcularStockTotal()} unidades</span>
                        </div>
                      </div>
                    </section>
                  </div>
                </div>

                <div className="form-actions">
                  <button 
                    className="save-button"
                    onClick={handleSave}
                    type="button"
                    disabled={formMode === 'nuevo' && !isSection5Complete()}
                  >
                    <Save size={18} />
                    <span>
                      {formMode === 'nuevo' ? 'Registrar Producto' : 'Añadir Lotes'} 
                      {' '}({lotes.length} lote{lotes.length !== 1 ? 's' : ''}, {calcularStockTotal()} uds)
                    </span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* CARGA MASIVA */}
        {activeSection === 'carga-masiva' && (
          <BulkUploadModal
            isOpen={true}
            onClose={() => setActiveSection('listado')}
            onSuccess={loadProducts}
            API_URL={API_URL}
            token={token}
          />
        )}

        {/* GESTIÓN DE CATEGORÍAS */}
        {activeSection === 'categorias' && (
          <div className="section-content">
            <GestionCategorias API_URL={API_URL} onClose={handleCloseGestion} token={token} />
          </div>
        )}

        {/* GESTIÓN DE FAMILIAS */}
        {activeSection === 'familias' && (
          <div className="section-content">
            <GestionFamilias API_URL={API_URL} onClose={handleCloseGestion} token={token} />
          </div>
        )}

        {/* GESTIÓN DE PROVEEDORES */}
        {activeSection === 'proveedores' && (
          <div className="section-content">
            <GestionProveedores API_URL={API_URL} onClose={handleCloseGestion} token={token} />
          </div>
        )}

        {activeSection === 'pwms' && (
          <div className="section-content">
            <ProductosAB token={token} />
          </div>
        )}

      </div>

      <style jsx>{`
        /* ESTILOS BASE */
        .inventario-page {
          min-height: 100vh;
          background: #F5F7FA;
        }

        .loading-state {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          min-height: 400px;
          gap: 16px;
        }

        .spinner {
          width: 40px;
          height: 40px;
          border: 3px solid #E5E7EB;
          border-top: 3px solid #3B82F6;
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        /* HEADER */
        .page-header {
          background: white;
          border-bottom: 1px solid #E5E7EB;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
        }

        .header-content {
          max-width: 1600px;
          margin: 0 auto;
          padding: 20px 24px;
        }

        .title-section {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .title-icon {
          color: #3B82F6;
          width: 28px;
          height: 28px;
        }

        .title-section h1 {
          margin: 0;
          font-size: 24px;
          font-weight: 600;
          color: #1A1A1A;
        }

        /* NAVEGACIÓN PRINCIPAL */
        .main-navigation {
          max-width: 1600px;
          margin: 0 auto;
          padding: 0 24px;
          display: flex;
          gap: 4px;
          overflow-x: auto;
          -webkit-overflow-scrolling: touch;
        }

        .nav-item {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 14px 20px;
          border: none;
          background: transparent;
          color: #64748B;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.3s ease;
          border-bottom: 3px solid transparent;
          white-space: nowrap;
          position: relative;
        }

        .nav-item:hover {
          color: #3B82F6;
          background: rgba(59, 130, 246, 0.05);
        }

        .nav-item.active {
          color: #3B82F6;
          border-bottom-color: #3B82F6;
          background: rgba(59, 130, 246, 0.05);
        }

        .nav-item svg {
          flex-shrink: 0;
        }

        /* CONTENIDO */
        .page-content {
          max-width: 1600px;
          margin: 0 auto;
          padding: 24px;
        }

        .section-content {
          background: white;
          border-radius: 12px;
          overflow: hidden;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
        }

        .form-container {
          background: white;
          border-radius: 12px;
          border: 1px solid #E5E7EB;
          overflow: hidden;
        }

        /* PESTAÑAS COMO TABS */
        .form-tabs {
          display: flex;
          background: #F8FAFC;
          border-bottom: 2px solid #E5E7EB;
        }

        .form-tab {
          flex: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          padding: 16px 24px;
          border: none;
          background: transparent;
          color: #64748B;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
          border-bottom: 3px solid transparent;
          position: relative;
        }

        .form-tab:hover {
          color: #3B82F6;
          background: rgba(59, 130, 246, 0.05);
        }

        .form-tab.active {
          color: #3B82F6;
          background: white;
          border-bottom-color: #3B82F6;
        }

        .form-tab svg {
          width: 16px;
          height: 16px;
        }

        /* SELECCIÓN DE PRODUCTO EXISTENTE */
        .product-selection-section {
          padding: 24px;
        }

        .products-table-wrapper {
          animation: fadeIn 0.3s ease-out;
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .table-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 16px;
        }

        .table-header h3 {
          margin: 0;
          font-size: 18px;
          font-weight: 600;
          color: #1A1A1A;
        }

        .search-box {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px 12px;
          border: 1px solid #E5E7EB;
          border-radius: 8px;
          background: white;
          min-width: 300px;
        }

        .search-box svg {
          color: #94A3B8;
          flex-shrink: 0;
        }

        .search-box input {
          border: none;
          outline: none;
          flex: 1;
          font-size: 13px;
          color: #1A1A1A;
        }

        .products-table-container {
          border: 1px solid #E5E7EB;
          border-radius: 8px;
          overflow-x: auto;
          -webkit-overflow-scrolling: touch;
        }

        .products-table {
          width: 100%;
          min-width: 800px;
          border-collapse: collapse;
        }

        .products-table thead {
          position: sticky;
          top: 0;
          background: #F8FAFC;
          z-index: 10;
        }

        .products-table th {
          padding: 12px 16px;
          text-align: left;
          font-size: 12px;
          font-weight: 600;
          color: #64748B;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          border-bottom: 1px solid #E5E7EB;
          white-space: nowrap;
        }

        .products-table tbody tr {
          border-bottom: 1px solid #F1F5F9;
          transition: all 0.2s;
        }

        .products-table tbody tr.selectable-row {
          cursor: pointer;
        }

        .products-table tbody tr.selectable-row:hover {
          background: rgba(59, 130, 246, 0.05);
        }

        .products-table td {
          padding: 12px 16px;
          font-size: 13px;
          color: #1A1A1A;
        }

        .product-image-cell {
          width: 48px;
          height: 48px;
          border-radius: 6px;
          overflow: hidden;
          border: 1px solid #E5E7EB;
        }

        .product-image-cell img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .no-image {
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #F8FAFC;
          color: #CBD5E1;
        }

        .barcode-cell {
          font-family: 'Courier New', monospace;
          font-weight: 600;
          color: #3B82F6;
        }

        .description-cell {
          max-width: 300px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .stock-cell {
          font-weight: 600;
          color: #10B981;
        }

        .lotes-cell {
          color: #8B5CF6;
          font-weight: 500;
        }

        .btn-delete-inline {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 32px;
          height: 32px;
          border-radius: 6px;
          border: 1px solid #E5E7EB;
          background: white;
          color: #EF4444;
          cursor: pointer;
          transition: all 0.2s;
        }

        .btn-delete-inline:hover {
          background: rgba(239, 68, 68, 0.1);
          border-color: #EF4444;
          transform: scale(1.1);
        }

        .no-results {
          padding: 60px 20px;
          text-align: center;
          color: #94A3B8;
        }

        .no-results svg {
          margin-bottom: 12px;
          opacity: 0.5;
        }

        .no-results p {
          margin: 0;
          font-size: 14px;
        }

        /* PRODUCTO SELECCIONADO */
        .selected-product-view {
          animation: slideIn 0.4s ease-out;
        }

        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateX(-20px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        .selected-product-header {
          margin-bottom: 20px;
        }

        .btn-back {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 8px 14px;
          border: 1px solid #E5E7EB;
          border-radius: 6px;
          background: white;
          color: #64748B;
          font-size: 13px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
        }

        .btn-back:hover {
          border-color: #3B82F6;
          color: #3B82F6;
          background: rgba(59, 130, 246, 0.05);
        }

        .selected-product-table {
          margin-top: 12px;
          border: 2px solid #DBEAFE;
          border-radius: 8px;
          overflow: hidden;
          background: white;
          box-shadow: 0 2px 8px rgba(59, 130, 246, 0.1);
        }

        .compact-product-table {
          width: 100%;
          border-collapse: collapse;
        }

        .compact-product-table thead {
          background: linear-gradient(135deg, #EFF6FF 0%, #DBEAFE 100%);
        }

        .compact-product-table th {
          padding: 10px 12px;
          text-align: left;
          font-size: 11px;
          font-weight: 700;
          color: #1E40AF;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          border-bottom: 2px solid #3B82F6;
        }

        .selected-product-row {
          background: white;
          transition: background 0.2s ease;
        }

        .selected-product-row:hover {
          background: #F0F9FF;
        }

        .selected-product-row td {
          padding: 12px;
          font-size: 13px;
          color: #1a1a1a;
          border-bottom: 1px solid #E5E7EB;
        }

        .product-image-cell-compact {
          width: 48px;
          height: 48px;
          border-radius: 6px;
          overflow: hidden;
          border: 1px solid #E5E7EB;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }

        .product-image-cell-compact img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .no-image-compact {
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #F8FAFC;
          color: #CBD5E1;
        }

        .barcode-compact {
          font-family: 'Courier New', monospace;
          font-weight: 600;
          color: #3B82F6;
          font-size: 12px;
        }

        .description-compact {
          max-width: 300px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          font-weight: 500;
        }

        .stock-compact {
          font-weight: 700;
          color: #10B981;
          font-size: 14px;
        }

        .lotes-compact {
          font-weight: 600;
          color: #8B5CF6;
        }

        .form-content {
          transition: all 0.3s;
        }

        .form-content.hidden {
          display: none;
        }

        /* LAYOUT PROGRESIVO */
        .form-layout-progressive {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 1px;
          background: #E5E7EB;
        }

        .form-column {
          background: white;
          padding: 20px;
        }

        .form-column.left-column {
          animation: slideInLeft 0.4s ease-out;
        }

        @keyframes slideInLeft {
          from {
            opacity: 0;
            transform: translateX(-20px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        .form-column.right-column {
          animation: slideInRight 0.4s ease-out;
        }

        @keyframes slideInRight {
          from {
            opacity: 0;
            transform: translateX(20px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        .form-column.right-column.full-width {
          grid-column: 1 / -1;
        }

        /* SECCIÓN PROGRESIVA */
        .form-section-progressive {
          margin-bottom: 24px;
          padding: 20px;
          border-radius: 12px;
          border: 2px solid #E5E7EB;
          background: white;
          position: relative;
          transition: all 0.3s ease;
        }

        .form-section-progressive.active {
          border-color: #3B82F6;
          box-shadow: 0 4px 12px rgba(59, 130, 246, 0.1);
        }

        .form-section-progressive.locked {
          background: #F9FAFB;
          border-color: #E5E7EB;
          opacity: 0.6;
        }

        .form-section-progressive.locked * {
          cursor: not-allowed !important;
        }

        .section-header-progressive {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          margin-bottom: 16px;
          position: relative;
        }

        .section-number {
          width: 32px;
          height: 32px;
          border-radius: 50%;
          background: #E5E7EB;
          color: #94A3B8;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 14px;
          font-weight: 700;
          flex-shrink: 0;
          transition: all 0.3s;
        }

        .section-number.active {
          background: linear-gradient(135deg, #3B82F6, #2563EB);
          color: white;
          box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4);
        }

        .section-title-group {
          flex: 1;
        }

        .section-title-progressive {
          margin: 0 0 4px 0;
          font-size: 16px;
          font-weight: 600;
          color: #1A1A1A;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .lock-icon {
          color: #94A3B8;
        }

        .section-status {
          display: flex;
          align-items: center;
          gap: 6px;
          margin-top: 4px;
        }

        .status-icon.complete {
          color: #10B981;
        }

        .status-text {
          font-size: 12px;
          color: #64748B;
          font-weight: 500;
        }

        .status-text.optional {
          color: #94A3B8;
          font-style: italic;
        }

        .help-button {
          width: 28px;
          height: 28px;
          border-radius: 50%;
          border: 1px solid #E5E7EB;
          background: white;
          color: #3B82F6;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.2s;
          flex-shrink: 0;
        }

        .help-button:hover {
          background: #3B82F6;
          color: white;
          border-color: #3B82F6;
          transform: scale(1.1);
        }

        .tooltip-popup {
          position: absolute;
          top: 40px;
          right: 0;
          width: 320px;
          padding: 14px 16px;
          background: white;
          border: 2px solid #3B82F6;
          border-radius: 10px;
          box-shadow: 0 8px 20px rgba(0, 0, 0, 0.15);
          z-index: 100;
          animation: tooltipFadeIn 0.2s ease-out;
        }

        @keyframes tooltipFadeIn {
          from {
            opacity: 0;
            transform: translateY(-8px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .tooltip-popup p {
          margin: 0 0 8px 0;
          font-size: 13px;
          color: #1A1A1A;
          line-height: 1.5;
        }

        .tooltip-popup strong {
          color: #3B82F6;
          font-weight: 600;
        }

        .tooltip-popup ul {
          margin: 8px 0 0 0;
          padding-left: 20px;
          list-style: disc;
        }

        .tooltip-popup li {
          font-size: 12px;
          color: #4A5568;
          margin-bottom: 4px;
          line-height: 1.4;
        }

        .help-note {
          margin-top: 8px;
          padding: 8px 10px;
          background: rgba(59, 130, 246, 0.05);
          border-radius: 6px;
          font-size: 11px;
          color: #3B82F6;
          font-style: italic;
        }

        .btn-add-lote-header {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 6px 12px;
          border: none;
          border-radius: 8px;
          background: #10B981;
          color: white;
          font-size: 12px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s;
          flex-shrink: 0;
        }

        .btn-add-lote-header:hover {
          background: #059669;
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
        }

        .form-grid, .form-grid-2, .form-grid-3 {
          display: grid;
          gap: 14px;
        }

        .form-grid {
          grid-template-columns: 1fr 1fr;
        }

        .form-grid-2 {
          grid-template-columns: repeat(2, 1fr);
        }

        .form-grid-3 {
          grid-template-columns: repeat(3, 1fr);
        }

        .form-group {
          display: flex;
          flex-direction: column;
          gap: 6px;
        }

        .form-group.full-width {
          grid-column: 1 / -1;
        }

        .form-group label {
          font-size: 12px;
          font-weight: 600;
          color: #4A5568;
        }

        .required {
          color: #EF4444;
        }

        .input {
          padding: 10px 12px;
          border: 1px solid #E5E7EB;
          border-radius: 8px;
          font-size: 14px;
          color: #1A1A1A;
          background: white;
          transition: all 0.2s;
        }

        .input:focus {
          outline: none;
          border-color: #3B82F6;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
        }

        .input:disabled {
          background: #F9FAFB;
          color: #94A3B8;
          cursor: not-allowed;
        }

        .input.small {
          padding: 8px 10px;
          font-size: 13px;
        }

        /* LOTES */
        .lotes-section {
          background: #F9FAFB;
          border-radius: 12px;
          padding: 20px;
        }

        .lotes-container {
          display: flex;
          flex-direction: column;
          gap: 14px;
          margin-bottom: 16px;
        }

        .lote-card {
          border: 2px solid #E5E7EB;
          border-radius: 10px;
          overflow: hidden;
          background: white;
          animation: scaleIn 0.2s ease-out;
          transition: all 0.3s ease;
        }

        .lote-card:hover {
          border-color: #3B82F6;
          box-shadow: 0 4px 12px rgba(59, 130, 246, 0.15);
        }

        @keyframes scaleIn {
          from {
            opacity: 0;
            transform: scale(0.95);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }

        .lote-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 12px 16px;
          background: linear-gradient(to right, #F8FAFC, #EFF6FF);
          border-bottom: 1px solid #E5E7EB;
        }

        .lote-title {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 13px;
          font-weight: 600;
          color: #1A1A1A;
        }

        .lote-title svg {
          color: #3B82F6;
        }

        .stock-badge {
          padding: 3px 10px;
          background: #3B82F6;
          color: white;
          border-radius: 12px;
          font-size: 11px;
          font-weight: 700;
        }

        .btn-delete-lote {
          padding: 6px;
          border: none;
          border-radius: 6px;
          background: transparent;
          color: #EF4444;
          cursor: pointer;
          transition: all 0.2s;
        }

        .btn-delete-lote:hover {
          background: rgba(239, 68, 68, 0.1);
        }

        .lote-body {
          padding: 16px;
        }

        .cantidades-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 12px;
          margin-top: 12px;
          padding-top: 12px;
          border-top: 1px dashed #E5E7EB;
        }

        .cantidad-item {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .cantidad-item label {
          font-size: 11px;
          font-weight: 600;
          color: #3B82F6;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .ubicacion-mode-section {
          margin-top: 14px;
          padding-top: 14px;
          border-top: 1px dashed #E5E7EB;
        }

        .ubicacion-mode-switch {
          display: flex;
          gap: 8px;
          margin-top: 8px;
        }

        .mode-btn {
          flex: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
          padding: 10px 12px;
          border: 1px solid #E5E7EB;
          border-radius: 8px;
          background: white;
          color: #64748B;
          font-size: 12px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
        }

        .mode-btn:hover:not(:disabled) {
          border-color: #3B82F6;
          color: #3B82F6;
          background: rgba(59, 130, 246, 0.05);
        }

        .mode-btn.active {
          border-color: #3B82F6;
          background: #3B82F6;
          color: white;
        }

        .mode-btn:disabled {
          cursor: not-allowed;
          opacity: 0.5;
        }

        .manual-ubicacion-form {
          margin-top: 12px;
          padding: 14px;
          background: rgba(59, 130, 246, 0.05);
          border-radius: 8px;
          border: 1px dashed #3B82F6;
          animation: expandIn 0.3s ease-out;
        }

        @keyframes expandIn {
          from {
            opacity: 0;
            max-height: 0;
          }
          to {
            opacity: 1;
            max-height: 500px;
          }
        }

        .ubicacion-auto-info {
          display: flex;
          align-items: center;
          gap: 6px;
          margin-top: 8px;
          padding: 8px 10px;
          background: rgba(16, 185, 129, 0.1);
          border-radius: 6px;
          font-size: 11px;
          color: #059669;
        }

        .lotes-resumen {
          padding: 14px;
          background: white;
          border-radius: 8px;
          border: 2px solid #E5E7EB;
        }

        .resumen-row {
          display: flex;
          justify-content: space-between;
          padding: 8px 0;
          font-size: 13px;
        }

        .resumen-row.highlighted {
          border-top: 1px solid #E5E7EB;
          padding-top: 12px;
          margin-top: 8px;
        }

        .resumen-label {
          color: #64748B;
          font-weight: 500;
        }

        .resumen-value {
          color: #1A1A1A;
          font-weight: 700;
        }

        .resumen-row.highlighted .resumen-value {
          color: #3B82F6;
          font-size: 16px;
        }

        .barcodes-section {
          margin-bottom: 16px;
          padding: 14px;
          background: #F8FAFC;
          border-radius: 8px;
          border: 1px solid #E2E8F0;
        }

        .barcodes-section .info-label {
          margin-bottom: 12px;
          font-size: 13px;
          font-weight: 600;
          color: #334155;
        }

        .barcodes-section .form-grid {
          gap: 12px;
        }

        .conversion-section {
          margin-top: 14px;
          padding-top: 14px;
          border-top: 1px solid #E5E7EB;
        }

        .info-label {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 11px;
          color: #64748B;
          margin-bottom: 10px;
          font-weight: 500;
        }

        /* Control de Calidad */
        .scan-box {
          border: 2px dashed #E5E7EB;
          border-radius: 8px;
          padding: 16px;
          background: #FAFAFA;
        }

        .scan-dropzone {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 24px 12px;
          border: 2px dashed #CBD5E1;
          border-radius: 6px;
          background: white;
          cursor: pointer;
          transition: all 0.2s;
        }

        .scan-dropzone:not(.disabled):hover {
          border-color: #3B82F6;
          background: rgba(59, 130, 246, 0.02);
        }

        .scan-dropzone.disabled {
          cursor: not-allowed;
          opacity: 0.5;
        }

        .scan-dropzone svg {
          color: #3B82F6;
          margin-bottom: 6px;
        }

        .scan-dropzone p {
          margin: 0;
          font-size: 13px;
          font-weight: 500;
          color: #1A1A1A;
        }

        .scan-dropzone span {
          font-size: 11px;
          color: #94A3B8;
        }

        .scan-processing {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          padding: 20px;
          background: rgba(59, 130, 246, 0.05);
          border-radius: 6px;
          color: #3B82F6;
          font-size: 12px;
        }

        .scan-result {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 12px;
          border-radius: 6px;
          border: 1px solid;
        }

        .scan-result.good {
          background: rgba(16, 185, 129, 0.05);
          border-color: #10B981;
        }

        .scan-result.defective {
          background: rgba(239, 68, 68, 0.05);
          border-color: #EF4444;
        }

        .result-icon {
          flex-shrink: 0;
        }

        .scan-result.good .result-icon {
          color: #10B981;
        }

        .scan-result.defective .result-icon {
          color: #EF4444;
        }

        .result-text {
          flex: 1;
          display: flex;
          flex-direction: column;
          gap: 3px;
        }

        .result-text strong {
          font-size: 12px;
          color: #1A1A1A;
        }

        .confidence {
          font-size: 11px;
          color: #64748B;
        }

        .rescan-btn {
          padding: 5px;
          border: 1px solid currentColor;
          border-radius: 6px;
          background: white;
          cursor: pointer;
          transition: all 0.2s;
        }

        .scan-result.good .rescan-btn {
          color: #10B981;
        }

        .scan-result.defective .rescan-btn {
          color: #EF4444;
        }

        .rescan-btn:hover {
          transform: rotate(180deg);
        }

        .file-hidden {
          display: none;
        }

        /* Imagen del producto */
        .image-upload-box {
          border: 2px dashed #E5E7EB;
          border-radius: 8px;
          padding: 16px;
          background: #FAFAFA;
        }

        .upload-mode-switch {
          display: flex;
          gap: 6px;
          background: #F1F5F9;
          padding: 4px;
          border-radius: 8px;
          width: fit-content;
          margin-bottom: 12px;
        }

        .mode-option {
          padding: 6px 14px;
          border: none;
          border-radius: 6px;
          background: transparent;
          color: #64748B;
          font-size: 12px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
        }

        .mode-option.active {
          background: white;
          color: #3B82F6;
          box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
        }

        .mode-option:disabled {
          cursor: not-allowed;
          opacity: 0.5;
        }

        .image-preview-wrapper {
          position: relative;
          width: 100%;
          height: 160px;
          border-radius: 6px;
          overflow: hidden;
          border: 1px solid #E5E7EB;
        }

        .image-preview-wrapper img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .remove-image {
          position: absolute;
          top: 8px;
          right: 8px;
          padding: 6px;
          border: none;
          border-radius: 50%;
          background: rgba(239, 68, 68, 0.9);
          color: white;
          cursor: pointer;
          transition: all 0.2s;
        }

        .remove-image:hover {
          background: #EF4444;
        }

        .upload-dropzone {
          display: flex;
          flex-direction: column;
          gap: 10px;
        }

        .dropzone-content {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 24px 12px;
          border: 2px dashed #CBD5E1;
          border-radius: 6px;
          background: white;
          cursor: pointer;
          transition: all 0.2s;
        }

        .dropzone-content:not(.disabled):hover {
          border-color: #3B82F6;
          background: rgba(59, 130, 246, 0.02);
        }

        .dropzone-content.disabled {
          cursor: not-allowed;
          opacity: 0.5;
        }

        .dropzone-content svg {
          color: #3B82F6;
          margin-bottom: 6px;
        }

        .dropzone-content p {
          margin: 0;
          font-size: 13px;
          font-weight: 500;
          color: #1A1A1A;
        }

        .dropzone-content span {
          font-size: 11px;
          color: #94A3B8;
        }

        .upload-confirm {
          padding: 8px 16px;
          border: none;
          border-radius: 6px;
          background: #3B82F6;
          color: white;
          font-size: 12px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
        }

        .upload-confirm:hover:not(:disabled) {
          background: #2563EB;
        }

        .upload-confirm:disabled {
          background: #CBD5E1;
          cursor: not-allowed;
        }

        .url-upload {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .form-actions {
          padding: 20px 24px;
          border-top: 1px solid #E5E7EB;
          display: flex;
          justify-content: flex-end;
          background: #F8FAFC;
        }

        .save-button {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 14px 36px;
          border: none;
          border-radius: 10px;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          font-size: 15px;
          font-weight: 700;
          cursor: pointer;
          transition: all 0.3s;
          box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
        }

        .save-button:hover:not(:disabled) {
          transform: translateY(-3px);
          box-shadow: 0 6px 20px rgba(102, 126, 234, 0.5);
        }

        .save-button:disabled {
          background: linear-gradient(135deg, #CBD5E1 0%, #94A3B8 100%);
          cursor: not-allowed;
          transform: none;
          box-shadow: none;
        }

        @media (max-width: 1200px) {
          .form-layout-progressive {
            grid-template-columns: 1fr;
          }

          .form-column.right-column {
            grid-column: 1;
          }
        }

        @media (max-width: 768px) {
          .main-navigation {
            padding: 0 12px;
          }

          .nav-item {
            padding: 12px 16px;
            font-size: 13px;
          }

          .page-content {
            padding: 16px;
          }

          .form-grid, .form-grid-2, .form-grid-3 {
            grid-template-columns: 1fr;
          }

          .cantidades-grid {
            grid-template-columns: 1fr;
          }

          .search-box {
            min-width: 100%;
          }

          .table-header {
            flex-direction: column;
            align-items: flex-start;
            gap: 12px;
          }

          .tooltip-popup {
            width: 280px;
          }
        }
      `}</style>
    </div>
  )
}

export default RegisterProduct